/*  global_dpm_allocations.tsx
    – The Global DPM allocation excel flow, all UI in one file:
      • GlobalDpmAllocationUploadModal: upload the excel (Order Date, Settlement
        Date, ISIN Number, Security Number, FUND Name, Transactions type,
        PF Number, Amount) into trade_dates/{orderDate}/global_dpm_allocations,
        tagged per upload (file name + timestamp), then immediately allocate
        already-settled matching trades (ops-uploaded-first case).
      • GlobalDpmAllocationRowsPanel: verification for the SELECTED date only —
        match status per order group (matched / error >1% / ambiguous / waiting
        with reason), manual apply, per-group delete, and the Uploads button.
      • GlobalDpmUploadsModal: uploads listed by file (name, time, counts) with
        per-upload delete of its pending rows.
      • GlobalDpmDateCards: replaces the pending-dates panel for eligible users —
        one card per date with the 4 trade states (pending / allocated / sent /
        error); click jumps to the date.
      The DPM-uploaded-first case is auto-allocated later by the tradeUpdated
      cloud function; every surface runs the same matcher
      (../../global_dpm_matching.ts).
*/
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Badge,
    Button,
    Card,
    Collapse,
    FileInput,
    Group,
    Loader,
    Modal,
    NumberFormatter,
    Paper,
    SimpleGrid,
    Stack,
    Table,
    Text,
    Title,
    UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconChevronDown, IconChevronUp, IconFiles, IconTrash, IconUpload, IconX } from '@tabler/icons-react';
import * as XLSX from 'xlsx';
import { PieChart } from '@mui/x-charts/PieChart';
import {
    Timestamp,
    collection,
    collectionGroup,
    doc,
    getDocs,
    onSnapshot,
    query,
    where,
    writeBatch,
} from 'firebase/firestore';
import { useFirestore } from '../../helpers/firebaseContext';
import { ConfigContext, MyUserDataContext } from '../../contexts';
import { ALLFUNDS_DEAD_STATUSES, GlobalDpmAllocationRow, Trade, qtyEq } from '../../types';
import {
    GLOBAL_DPM_MATCH_TOLERANCE,
    GlobalDpmMatchGroup,
    GlobalDpmProposal,
    GlobalDpmWaitingReason,
    MatchableTrade,
    globalDpmReferenceAmount,
    matchGlobalDpmRows,
    parseDdMmYyyyText,
    toDdMmYyyy,
    tradeToMatchable,
} from '../../global_dpm_matching';

const ROWS_SUBCOLLECTION = 'global_dpm_allocations';
const GLOBAL_DPM_ACCOUNT = 'GLOBAL_DPM';
export const BATCH_LIMIT = 400; // also used by the rebalance batches tab

const fmtPct = (e: number) => `${(e * 100).toFixed(2)}%`;

export const rowRef = (db: ReturnType<typeof useFirestore>, r: { valueDate: string; id: string }) =>
    doc(db, 'trade_dates', r.valueDate, ROWS_SUBCOLLECTION, r.id);

/* "39,860.00" → 39860; raw cells are already numbers */
const num = (v: unknown): number => {
    if (typeof v === 'number') return v;
    const n = Number(String(v ?? '').replace(/,/g, '').trim());
    return Number.isFinite(n) ? n : 0;
};

/* Real date cells arrive as Excel serial numbers (raw:true) — converted via
   SSF.parse_date_code, which has no timezone or locale involvement (cellDates
   Date objects shift a day across timezones; raw:false reformats to US m/d/yy).
   Text cells stay as typed: dd/MM/yyyy (strict, see parseDdMmYyyyText). */
const parseDMY = (v: unknown): Date | null => {
    if (typeof v === 'number') {
        const d = XLSX.SSF.parse_date_code(v);
        return d ? new Date(d.y, d.m - 1, d.d) : null;
    }
    if (v instanceof Date) return isNaN(v.getTime()) ? null : new Date(v.getFullYear(), v.getMonth(), v.getDate());
    return parseDdMmYyyyText(String(v ?? ''));
};

const WAITING_REASON_TEXT: Record<GlobalDpmWaitingReason, string> = {
    'no-trade': 'waiting — trade not uploaded by operations yet',
    'trade-already-allocated': 'trade already allocated',
    'trade-locked': 'trade already sent to Geneva (LOCKED)',
    'trade-not-settled': 'trade not settled at AllFunds yet',
    'mixed-basis': 'rows mix quantity-only and amount-only — fix the excel',
};

const fmtQty = (q: number) => <NumberFormatter value={q} thousandSeparator decimalScale={6} />;

/* Write proposals: allocations onto the trade docs (commissions get recomputed by
   the tradeUpdated function, same as every other client allocation write) + flip
   the consumed rows to allocated. */
const applyProposals = async (db: ReturnType<typeof useFirestore>, proposals: GlobalDpmProposal[]) => {
    // One batch per proposal: keeps each trade and its rows atomic while never
    // approaching the 500-write batch limit on dates with many proposals/rows.
    for (const p of proposals) {
        const batch = writeBatch(db);
        batch.update(doc(db, 'trade_dates', p.trade.valueDate, 'trades', p.trade.id), { allocations: p.allocations });
        for (const row of p.group.rows) {
            batch.update(rowRef(db, { valueDate: p.group.valueDate, id: row.id }), {
                status: 'allocated',
                allocatedTradeId: p.trade.id,
                allocatedAt: Timestamp.now(),
                // the rebalance batch's buy leg reads proceeds from these (rows are the ledger)
                allocatedQty: p.allocations.find((a) => a.portfolioId === row.portfolioId)?.qty ?? 0,
                allocatedNav: p.trade.nav ?? 0,
            });
        }
        await batch.commit();
    }
};

export const deletePendingRows = async (db: ReturnType<typeof useFirestore>, rows: { valueDate: string; id: string }[]) => {
    for (let i = 0; i < rows.length; i += BATCH_LIMIT) {
        const batch = writeBatch(db);
        rows.slice(i, i + BATCH_LIMIT).forEach((r) => batch.delete(rowRef(db, r)));
        await batch.commit();
    }
};

const useAutoApply = () => {
    const configContext = useContext(ConfigContext);
    return !!configContext.tradesMap?.accounts?.[GLOBAL_DPM_ACCOUNT]?.allocateFromExcel;
};

/* ============================== Upload modal ============================== */

interface UploadResult {
    allocatedNow: number;
    pending: number;
    error: number;
    ambiguous: number;
}

interface UploadModalProps {
    opened: boolean;
    setOpened: (o: boolean) => void;
}

interface ParsedDpmRow extends Omit<GlobalDpmAllocationRow, 'allocatedAt' | 'uploadedAt'> {
    action: 'create' | 'update' | 'skip';
}

export const GlobalDpmAllocationUploadModal = ({ opened, setOpened }: UploadModalProps) => {
    const db = useFirestore();
    const userContext = useContext(MyUserDataContext);
    const userEmail = userContext.user?.email || 'unknown';
    const autoApply = useAutoApply();

    const [file, setFile] = useState<File | null>(null);
    const [processing, setProcessing] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [rows, setRows] = useState<ParsedDpmRow[]>([]);
    const [result, setResult] = useState<UploadResult | null>(null);

    const reset = () => {
        setErrors([]);
        setRows([]);
        setResult(null);
    };

    const handleClose = () => {
        setFile(null);
        reset();
        setOpened(false);
    };

    const handleFileUpload = async (f: File) => {
        setFile(f);
        setProcessing(true);
        reset();
        try {
            const workbook = XLSX.read(await f.arrayBuffer(), { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            /* raw values: date cells stay Excel serial numbers (see parseDMY) */
            const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });

            const uploadId = `${f.name}@${Date.now()}`;
            const err: string[] = [];
            const parsed: Omit<ParsedDpmRow, 'action'>[] = [];
            const idCounts = new Map<string, number>();
            json.forEach((raw, i) => {
                const line = i + 2; // 1-based + header row
                const isin = String(raw['ISIN Number'] ?? '').trim();
                const pf = String(raw['PF Number'] ?? '').trim();
                if (!isin && !pf) return; // blank filler row
                /* the excel's date column is the trade's ORDER date; the team is asked to
                   head it "Order Date" but the legacy "Value Date" header still works */
                const orderDate = parseDMY(raw['Order Date'] ?? raw['Value Date']);
                const type = String(raw['Transactions type'] ?? '').trim();
                const amount = num(raw['Amount']);
                const quantity = num(raw['Quantity']);
                /* quantity first, then amount — at least one must be positive, neither negative */
                const hasSize = amount >= 0 && quantity >= 0 && (amount > 0 || quantity > 0);
                /* Explicit side vocabulary — an unknown type must never silently default
                   to BUY (it would match and allocate against the wrong-side trade). */
                const typeUpper = type.toUpperCase();
                const side: 'BUY' | 'SELL' | null =
                    typeUpper.includes('SUBS') || /^(B$|BUY|P$|PURCH)/.test(typeUpper) ? 'BUY'
                        : typeUpper.includes('REDEMPTION') || /^(S$|SELL|SALE)/.test(typeUpper) ? 'SELL'
                            : null;

                if (!orderDate) err.push(`Row ${line}: invalid Order Date "${raw['Order Date'] ?? raw['Value Date']}" (expected dd/mm/yyyy)`);
                if (!isin) err.push(`Row ${line}: missing ISIN Number`);
                if (!hasSize) err.push(`Row ${line}: needs a positive Quantity or Amount (Quantity "${raw['Quantity']}", Amount "${raw['Amount']}")`);
                if (!side) err.push(`Row ${line}: unrecognized Transactions type "${type}" — expected a buy (BUY/PURCHASE/SUBSCRIPTION) or sell (SELL/SALE/REDEMPTION) value`);
                if (!pf) err.push(`Row ${line}: missing PF Number`);
                if (!orderDate || !isin || !pf || !hasSize || !side) return;

                const dateId = toDdMmYyyy(orderDate);
                /* Amount deliberately NOT part of the id: a corrected re-upload (same
                   order, fixed amount) must overwrite the same doc instead of stranding
                   the old amount as a stale pending row that inflates the group sum. */
                const baseId = `${isin}_${pf}_${side}`;
                const n = idCounts.get(`${dateId}|${baseId}`) ?? 0;
                idCounts.set(`${dateId}|${baseId}`, n + 1);
                parsed.push({
                    id: `${baseId}_${n}`,
                    valueDate: dateId,
                    isin,
                    securityNumber: String(raw['Security Number'] ?? '').trim(),
                    fundName: String(raw['FUND Name'] ?? '').trim(),
                    side,
                    portfolioId: pf,
                    amount,
                    quantity,
                    status: 'pending',
                    uploadId,
                    uploadFileName: f.name,
                    uploadedBy: userEmail,
                });
            });
            if (parsed.length === 0) err.push('No allocation rows found — is this the Global DPM allocation file?');

            /* diff against the affected dates' existing rows: idempotent re-uploads,
               allocated rows never reset */
            const dates = [...new Set(parsed.map((r) => r.valueDate))];
            const existing = new Map<string, GlobalDpmAllocationRow>();
            await Promise.all(dates.map(async (dateId) => {
                const snap = await getDocs(collection(db, 'trade_dates', dateId, ROWS_SUBCOLLECTION));
                snap.docs.forEach((d) => existing.set(`${dateId}|${d.id}`, d.data() as GlobalDpmAllocationRow));
            }));
            /* a rebalance batch writes its rows itself (ids end in _r{round}) and also downloads them in this
               layout: uploading that file here must not add a second pending row for the same order line */
            const batchWritten = new Set([...existing].filter(([k]) => /_r\d+$/.test(k)).map(([, x]) => `${x.valueDate}|${x.isin}|${x.portfolioId}|${x.side}`));
            setRows(parsed.map((r) => {
                const prev = existing.get(`${r.valueDate}|${r.id}`);
                const skip = prev?.status === 'allocated' || (!prev && batchWritten.has(`${r.valueDate}|${r.isin}|${r.portfolioId}|${r.side}`));
                return { ...r, action: skip ? 'skip' : prev ? 'update' : 'create' };
            }));
            setErrors(err);
        } catch (e: any) {
            console.error(e);
            setErrors([e.message || 'Failed to parse spreadsheet']);
        }
        setProcessing(false);
    };

    const submitData = async () => {
        const writable = rows.filter((r) => r.action !== 'skip');
        if (writable.length === 0) return;
        setUpdating(true);
        try {
            for (let i = 0; i < writable.length; i += BATCH_LIMIT) {
                const batch = writeBatch(db);
                for (const { action: _action, ...row } of writable.slice(i, i + BATCH_LIMIT)) {
                    batch.set(rowRef(db, row), { ...row, uploadedAt: Timestamp.now() }, { merge: true });
                }
                await batch.commit();
            }

            /* ops-uploaded-first case: allocate already-settled matching trades per affected
               order date. Trades are bucketed by SETTLEMENT date but matched by ORDER date,
               so one collection-group query fetches them all — the matcher keys them by
               order date. Within-tolerance matches are always applied automatically;
               only >1% mismatches wait for manual review. */
            const summary: UploadResult = { allocatedNow: 0, pending: 0, error: 0, ambiguous: 0 };
            const dates = [...new Set(writable.map((r) => r.valueDate))];
            const tradesSnap = await getDocs(query(collectionGroup(db, 'trades'), where('accountId', '==', GLOBAL_DPM_ACCOUNT)));
            const matchableTrades = tradesSnap.docs.map((d) => tradeToMatchable(d.data() as Trade, d.ref.parent.parent!.id));
            for (const dateId of dates) {
                const rowsSnap = await getDocs(query(collection(db, 'trade_dates', dateId, ROWS_SUBCOLLECTION), where('status', '==', 'pending')));
                const pendingRows = rowsSnap.docs.map((d) => ({ ...(d.data() as GlobalDpmAllocationRow), id: d.id }));
                const match = matchGlobalDpmRows(pendingRows, matchableTrades);
                if (autoApply && match.proposals.length > 0) {
                    await applyProposals(db, match.proposals);
                    summary.allocatedNow += match.proposals.reduce((s, p) => s + p.group.rows.length, 0);
                } else {
                    summary.pending += match.proposals.reduce((s, p) => s + p.group.rows.length, 0);
                }
                summary.pending += match.waiting.reduce((s, g) => s + g.rows.length, 0);
                summary.error += match.mismatches.reduce((s, p) => s + p.group.rows.length, 0);
                summary.ambiguous += match.ambiguous.reduce((s, g) => s + g.rows.length, 0);
            }
            setResult(summary);
            notifications.show({
                title: 'Global DPM allocations uploaded',
                message: `${writable.length} rows saved${summary.allocatedNow ? `, ${summary.allocatedNow} allocated immediately` : ''}`,
                icon: <IconCheck />,
                color: 'green',
            });
            setRows([]);
            setFile(null);
        } catch (e) {
            console.error(e);
            notifications.show({ title: 'Upload failed', message: 'Check console', icon: <IconX />, color: 'red' });
        }
        setUpdating(false);
    };

    const counts = { create: 0, update: 0, skip: 0 };
    rows.forEach((r) => counts[r.action]++);

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            size="xl"
            centered
            title={<Group><IconUpload size="1.2rem" /><Title order={4}>Upload Global DPM Allocations</Title></Group>}
        >
            <Stack gap="md">
                <FileInput
                    label="Global DPM allocation excel"
                    placeholder="Choose .xls or .xlsx file"
                    accept=".xls,.xlsx"
                    value={file}
                    onChange={(f) => f && handleFileUpload(f)}
                    disabled={processing || updating}
                />
                {!autoApply && (
                    <Text size="sm" c="orange">
                        Auto-allocation from excel is switched off in trades settings — rows will be stored as pending only.
                    </Text>
                )}
                {processing && <Group><Loader size="sm" /><Text>Parsing…</Text></Group>}

                {errors.length > 0 && (
                    <Stack gap={2}>
                        <Text c="red" fw={700}>Errors:</Text>
                        {errors.map((msg, i) => <Text key={i} c="red" size="sm">- {msg}</Text>)}
                    </Stack>
                )}

                {rows.length > 0 && (
                    <>
                        <Group gap="lg">
                            <Text><Text span fw={700}>Rows:</Text> {rows.length}</Text>
                            <Badge color="green" variant="light">new: {counts.create}</Badge>
                            <Badge color="blue" variant="light">update: {counts.update}</Badge>
                            {counts.skip > 0 && <Badge color="gray" variant="light" title="already allocated, or already written by a rebalance batch">skipped: {counts.skip}</Badge>}
                        </Group>
                        <Table highlightOnHover>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Order Date</Table.Th>
                                    <Table.Th>ISIN</Table.Th>
                                    <Table.Th>Fund</Table.Th>
                                    <Table.Th>Side</Table.Th>
                                    <Table.Th>PF</Table.Th>
                                    <Table.Th>Quantity</Table.Th>
                                    <Table.Th>Amount</Table.Th>
                                    <Table.Th>Action</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {rows.map((r) => (
                                    <Table.Tr key={`${r.valueDate}|${r.id}`}>
                                        <Table.Td>{r.valueDate}</Table.Td>
                                        <Table.Td>{r.isin}</Table.Td>
                                        <Table.Td>{r.fundName}</Table.Td>
                                        <Table.Td>{r.side}</Table.Td>
                                        <Table.Td>{r.portfolioId}</Table.Td>
                                        <Table.Td>{fmtQty(r.quantity ?? 0)}</Table.Td>
                                        <Table.Td><NumberFormatter value={r.amount} thousandSeparator decimalScale={2} /></Table.Td>
                                        <Table.Td>
                                            <Badge color={r.action === 'create' ? 'green' : r.action === 'update' ? 'blue' : 'gray'} variant="light">
                                                {r.action}
                                            </Badge>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                        <Button onClick={submitData} loading={updating} disabled={errors.length > 0 || counts.create + counts.update === 0}>
                            Save {counts.create + counts.update} rows{autoApply ? ' & allocate matches' : ''}
                        </Button>
                    </>
                )}

                {result && (
                    <Group gap="lg">
                        <Text fw={700}>Result:</Text>
                        <Badge color="green" variant="light">allocated now: {result.allocatedNow}</Badge>
                        <Badge color="yellow" variant="light">pending: {result.pending}</Badge>
                        {result.error > 0 && <Badge color="red" variant="light">error &gt;{fmtPct(GLOBAL_DPM_MATCH_TOLERANCE)}: {result.error}</Badge>}
                        {result.ambiguous > 0 && <Badge color="orange" variant="light">ambiguous: {result.ambiguous}</Badge>}
                        <Text size="sm" c="dimmed">Details in the verification panel under the trades table.</Text>
                    </Group>
                )}
            </Stack>
        </Modal>
    );
};

/* ============================== Uploads modal ============================== */

interface UploadsModalProps {
    opened: boolean;
    setOpened: (o: boolean) => void;
}

export const GlobalDpmUploadsModal = ({ opened, setOpened }: UploadsModalProps) => {
    const db = useFirestore();
    const [allRows, setAllRows] = useState<GlobalDpmAllocationRow[] | null>(null);
    const [busy, setBusy] = useState(false);
    const [expandedUpload, setExpandedUpload] = useState<string | null>(null);

    useEffect(() => {
        if (!opened) return;
        return onSnapshot(collectionGroup(db, ROWS_SUBCOLLECTION), (snap) =>
            setAllRows(snap.docs.map((d) => ({ ...(d.data() as GlobalDpmAllocationRow), id: d.id }))));
    }, [db, opened]);

    const uploads = useMemo(() => {
        const byUpload = new Map<string, GlobalDpmAllocationRow[]>();
        (allRows ?? []).forEach((r) => {
            const key = r.uploadId || 'unknown';
            (byUpload.get(key) ?? byUpload.set(key, []).get(key)!).push(r);
        });
        return [...byUpload.entries()]
            .map(([uploadId, rows]) => ({
                uploadId,
                fileName: rows[0].uploadFileName || uploadId.split('@')[0],
                uploadedBy: rows[0].uploadedBy,
                uploadedAt: rows.reduce<Timestamp | undefined>((max, r) =>
                    !max || (r.uploadedAt && r.uploadedAt.toMillis() > max.toMillis()) ? r.uploadedAt : max, undefined),
                rows,
                pending: rows.filter((r) => r.status === 'pending').length,
                allocated: rows.filter((r) => r.status === 'allocated').length,
                dates: [...new Set(rows.map((r) => r.valueDate))].sort(),
            }))
            .sort((a, b) => (b.uploadedAt?.toMillis() ?? 0) - (a.uploadedAt?.toMillis() ?? 0));
    }, [allRows]);

    const deleteUpload = async (upload: typeof uploads[number]) => {
        const pendingRows = upload.rows.filter((r) => r.status === 'pending');
        if (!window.confirm(`Delete ${pendingRows.length} pending row(s) of "${upload.fileName}"? ${upload.allocated ? `${upload.allocated} allocated row(s) are kept as history.` : ''}`)) return;
        setBusy(true);
        try {
            await deletePendingRows(db, pendingRows);
            notifications.show({ title: 'Upload deleted', message: `${pendingRows.length} pending row(s) removed`, icon: <IconCheck />, color: 'green' });
        } catch (e) {
            console.error(e);
            notifications.show({ title: 'Delete failed', message: 'Check console', icon: <IconX />, color: 'red' });
        }
        setBusy(false);
    };

    return (
        <Modal
            opened={opened}
            onClose={() => setOpened(false)}
            size="xl"
            centered
            title={<Group><IconFiles size="1.2rem" /><Title order={4}>Global DPM Uploads</Title></Group>}
        >
            {allRows === null ? (
                <Group><Loader size="sm" /><Text>Loading…</Text></Group>
            ) : uploads.length === 0 ? (
                <Text c="dimmed">No uploads yet.</Text>
            ) : (
                <Table highlightOnHover>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>File</Table.Th>
                            <Table.Th>Uploaded</Table.Th>
                            <Table.Th>Dates</Table.Th>
                            <Table.Th>Rows</Table.Th>
                            <Table.Th>Status</Table.Th>
                            <Table.Th></Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {uploads.map((u) => (
                            <React.Fragment key={u.uploadId}>
                                <Table.Tr style={{ cursor: 'pointer' }} onClick={() => setExpandedUpload(expandedUpload === u.uploadId ? null : u.uploadId)}>
                                    <Table.Td>{u.fileName}</Table.Td>
                                    <Table.Td>
                                        <Text size="sm">{u.uploadedAt ? u.uploadedAt.toDate().toLocaleString() : '—'}</Text>
                                        <Text size="xs" c="dimmed">{u.uploadedBy}</Text>
                                    </Table.Td>
                                    <Table.Td><Text size="xs">{u.dates.join(', ')}</Text></Table.Td>
                                    <Table.Td>{u.rows.length}</Table.Td>
                                    <Table.Td>
                                        <Group gap={4}>
                                            {u.pending > 0 && <Badge color="yellow" variant="light">pending: {u.pending}</Badge>}
                                            {u.allocated > 0 && <Badge color="green" variant="light">allocated: {u.allocated}</Badge>}
                                        </Group>
                                    </Table.Td>
                                    <Table.Td>
                                        {u.pending > 0 && (
                                            <Button
                                                size="compact-xs"
                                                variant="subtle"
                                                color="red"
                                                leftSection={<IconTrash size={12} />}
                                                loading={busy}
                                                onClick={(e) => { e.stopPropagation(); deleteUpload(u); }}
                                            >
                                                Delete
                                            </Button>
                                        )}
                                    </Table.Td>
                                </Table.Tr>
                                {expandedUpload === u.uploadId && (
                                    <Table.Tr>
                                        <Table.Td colSpan={6}>
                                            <Table>
                                                <Table.Tbody>
                                                    {u.rows.map((r) => (
                                                        <Table.Tr key={`${r.valueDate}|${r.id}`}>
                                                            <Table.Td>{r.valueDate}</Table.Td>
                                                            <Table.Td>{r.isin}</Table.Td>
                                                            <Table.Td>{r.fundName}</Table.Td>
                                                            <Table.Td>{r.side}</Table.Td>
                                                            <Table.Td>{r.portfolioId}</Table.Td>
                                                            <Table.Td>{fmtQty(r.quantity ?? 0)}</Table.Td>
                                                            <Table.Td><NumberFormatter value={r.amount} thousandSeparator decimalScale={2} /></Table.Td>
                                                            <Table.Td>
                                                                <Badge color={r.status === 'allocated' ? 'green' : 'yellow'} variant="light">{r.status}</Badge>
                                                                {r.allocatedTradeId && <Text size="xs" c="dimmed">{r.allocatedTradeId}</Text>}
                                                            </Table.Td>
                                                        </Table.Tr>
                                                    ))}
                                                </Table.Tbody>
                                            </Table>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </React.Fragment>
                        ))}
                    </Table.Tbody>
                </Table>
            )}
        </Modal>
    );
};

/* ========================= Verification panel (selected date) ========================= */

interface RowsPanelProps {
    date: Date | null;
}

export const GlobalDpmAllocationRowsPanel = ({ date }: RowsPanelProps) => {
    const db = useFirestore();
    const autoApply = useAutoApply();
    const dateId = date ? toDdMmYyyy(date) : null;

    const [rows, setRows] = useState<GlobalDpmAllocationRow[]>([]);
    /* trades are bucketed by SETTLEMENT date but matched by ORDER date, so the
       selected date's own trades aren't enough — subscribe to all GLOBAL_DPM
       trades and let the matcher key them by order date */
    const [matchableTrades, setMatchableTrades] = useState<MatchableTrade[]>([]);
    const [expanded, setExpanded] = useState(true);
    const [busy, setBusy] = useState(false);
    const [uploadsOpened, setUploadsOpened] = useState(false);

    useEffect(() => {
        setRows([]);
        if (!dateId) return;
        return onSnapshot(collection(db, 'trade_dates', dateId, ROWS_SUBCOLLECTION), (snap) =>
            setRows(snap.docs.map((d) => ({ ...(d.data() as GlobalDpmAllocationRow), id: d.id }))));
    }, [db, dateId]);

    useEffect(() => {
        const q = query(collectionGroup(db, 'trades'), where('accountId', '==', GLOBAL_DPM_ACCOUNT));
        return onSnapshot(q, (snap) =>
            setMatchableTrades(snap.docs.map((d) => tradeToMatchable(d.data() as Trade, d.ref.parent.parent!.id))));
    }, [db]);

    const pending = useMemo(() => rows.filter((r) => r.status === 'pending'), [rows]);
    const allocated = useMemo(() => rows.filter((r) => r.status === 'allocated'), [rows]);
    const match = useMemo(() => matchGlobalDpmRows(pending, matchableTrades), [pending, matchableTrades]);

    /* within-tolerance matches need no manual "Apply split" — apply them the moment
       they appear; >1% mismatches and ambiguous groups always stay manual */
    const autoApplying = useRef(false);
    useEffect(() => {
        if (!autoApply || autoApplying.current || match.proposals.length === 0) return;
        autoApplying.current = true;
        applyProposals(db, match.proposals)
            .catch((e) => console.error('Global DPM auto-apply failed', e))
            .finally(() => { autoApplying.current = false; });
    }, [autoApply, match, db]);

    const rowsById = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);

    if (!dateId || rows.length === 0) {
        /* still surface the Uploads modal entry point even when this date has no rows */
        return null;
    }

    const apply = async (proposal: GlobalDpmProposal, confirmText?: string) => {
        if (confirmText && !window.confirm(confirmText)) return;
        setBusy(true);
        try {
            await applyProposals(db, [proposal]);
            notifications.show({
                title: 'Allocated',
                message: `${proposal.trade.id} split over ${proposal.allocations.length} portfolio(s)`,
                icon: <IconCheck />,
                color: 'green',
            });
        } catch (e) {
            console.error(e);
            notifications.show({ title: 'Allocation failed', message: 'Check console', icon: <IconX />, color: 'red' });
        }
        setBusy(false);
    };

    const deleteGroup = async (groupRows: { id: string }[], label: string) => {
        if (!window.confirm(`Delete ${groupRows.length} pending row(s) for ${label}?`)) return;
        setBusy(true);
        try {
            await deletePendingRows(db, groupRows.map((r) => ({ valueDate: dateId, id: r.id })));
            notifications.show({ title: 'Deleted', message: `${groupRows.length} row(s) removed`, icon: <IconCheck />, color: 'green' });
        } catch (e) {
            console.error(e);
            notifications.show({ title: 'Delete failed', message: 'Check console', icon: <IconX />, color: 'red' });
        }
        setBusy(false);
    };

    const renderRows = (g: { rows: { id: string; portfolioId: string; amount: number; quantity?: number }[] }, proposal?: GlobalDpmProposal) =>
        g.rows.map((r) => (
            <Text size="xs" key={r.id}>
                {r.portfolioId}: <NumberFormatter value={r.amount} thousandSeparator decimalScale={2} />
                {(r.quantity ?? 0) > 0 && <> · {fmtQty(r.quantity!)} u</>}
                {proposal && (
                    <>
                        {' → '}
                        {fmtQty(proposal.allocations.find((a) => a.portfolioId === r.portfolioId)?.qty ?? 0)} units
                    </>
                )}
            </Text>
        ));

    /* the group's reconciliation figure on its own basis: Σ units vs the trade's filledQty, or Σ amount vs the reference amount */
    const groupSum = (g: GlobalDpmMatchGroup) => g.basis === 'quantity'
        ? <>{fmtQty(g.quantitySum)} u</>
        : <NumberFormatter value={g.amountSum} thousandSeparator decimalScale={2} />;
    const tradeSum = (p: GlobalDpmProposal) => p.group.basis === 'quantity'
        ? <>{fmtQty(p.trade.filledQty)} u</>
        : <NumberFormatter value={globalDpmReferenceAmount(p.trade)} thousandSeparator decimalScale={2} />;

    const deleteButton = (g: { rows: { id: string }[]; isin: string }) => (
        <Button
            size="compact-xs"
            variant="subtle"
            color="red"
            leftSection={<IconTrash size={12} />}
            loading={busy}
            onClick={() => deleteGroup(g.rows, `${g.isin} ${dateId}`)}
        >
            Delete
        </Button>
    );

    return (
        <Card shadow="sm" p="md" radius="md" withBorder mt="md">
            <UnstyledButton w="100%" onClick={() => setExpanded((e) => !e)}>
                <Group justify="space-between" wrap="nowrap">
                    <Group gap="sm">
                        <Title order={5}>Global DPM Allocations</Title>
                        {pending.length > 0 && <Badge color="yellow" variant="light">{pending.length} pending</Badge>}
                        {match.mismatches.length > 0 && <Badge color="red" variant="light">{match.mismatches.length} error</Badge>}
                        {match.ambiguous.length > 0 && <Badge color="orange" variant="light">{match.ambiguous.length} ambiguous</Badge>}
                        {allocated.length > 0 && <Badge color="green" variant="light">{allocated.length} allocated</Badge>}
                        {!autoApply && <Badge color="gray" variant="light">auto-allocation off</Badge>}
                    </Group>
                    {expanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                </Group>
            </UnstyledButton>

            <Collapse expanded={expanded}>
                <Stack gap="sm" mt="sm">
                    <Group gap="xs">
                        <Button size="compact-sm" variant="light" leftSection={<IconFiles size={14} />} onClick={() => setUploadsOpened(true)}>
                            Uploads
                        </Button>
                    </Group>

                    <Table highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>ISIN</Table.Th>
                                <Table.Th>Fund</Table.Th>
                                <Table.Th>Side</Table.Th>
                                <Table.Th>Rows (PF: amount · qty)</Table.Th>
                                <Table.Th>Σ Amount / Σ Qty</Table.Th>
                                <Table.Th>Status</Table.Th>
                                <Table.Th>Actions</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {[...match.proposals.map((p) => ({ p, error: false })), ...match.mismatches.map((p) => ({ p, error: true }))].map(({ p, error }) => (
                                <Table.Tr key={p.group.key}>
                                    <Table.Td>{p.group.isin}</Table.Td>
                                    <Table.Td>{rowsById.get(p.group.rows[0].id)?.fundName ?? ''}</Table.Td>
                                    <Table.Td>{p.group.side}</Table.Td>
                                    <Table.Td>{renderRows(p.group, p)}</Table.Td>
                                    <Table.Td>
                                        {groupSum(p.group)}
                                        <Text size="xs" c={error ? 'red' : 'dimmed'}>
                                            trade: {tradeSum(p)} ({fmtPct(p.errorPct)})
                                        </Text>
                                    </Table.Td>
                                    <Table.Td>
                                        <Badge color={error ? 'red' : 'green'} variant="light">
                                            {error ? `Error > ${fmtPct(GLOBAL_DPM_MATCH_TOLERANCE)}` : 'Matched'}
                                        </Badge>
                                    </Table.Td>
                                    <Table.Td>
                                        <Group gap="xs" wrap="nowrap">
                                            <Button
                                                size="compact-xs"
                                                color={error ? 'red' : 'green'}
                                                loading={busy}
                                                onClick={() => apply(p, error
                                                    ? (p.group.basis === 'quantity'
                                                        ? `Quantity error is ${fmtPct(p.errorPct)} (excel ${p.group.quantitySum} vs trade ${p.trade.filledQty} units). Apply the proportional split to ${p.trade.id} anyway?`
                                                        : `Amount error is ${fmtPct(p.errorPct)} (excel ${p.group.amountSum} vs trade ${globalDpmReferenceAmount(p.trade)}). Apply the proportional split to ${p.trade.id} anyway?`)
                                                    : undefined)}
                                            >
                                                Apply split
                                            </Button>
                                            {deleteButton(p.group)}
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                            {match.ambiguous.map((g) => (
                                <Table.Tr key={g.key}>
                                    <Table.Td>{g.isin}</Table.Td>
                                    <Table.Td>{rowsById.get(g.rows[0].id)?.fundName ?? ''}</Table.Td>
                                    <Table.Td>{g.side}</Table.Td>
                                    <Table.Td>{renderRows(g)}</Table.Td>
                                    <Table.Td>{groupSum(g)}</Table.Td>
                                    <Table.Td><Badge color="orange" variant="light">Ambiguous — allocate manually</Badge></Table.Td>
                                    <Table.Td>{deleteButton(g)}</Table.Td>
                                </Table.Tr>
                            ))}
                            {match.waiting.map((g) => (
                                <Table.Tr key={g.key}>
                                    <Table.Td>{g.isin}</Table.Td>
                                    <Table.Td>{rowsById.get(g.rows[0].id)?.fundName ?? ''}</Table.Td>
                                    <Table.Td>{g.side}</Table.Td>
                                    <Table.Td>{renderRows(g)}</Table.Td>
                                    <Table.Td>{groupSum(g)}</Table.Td>
                                    <Table.Td>
                                        <Badge color={g.reason === 'no-trade' ? 'yellow' : g.reason === 'mixed-basis' ? 'red' : 'orange'} variant="light">
                                            {WAITING_REASON_TEXT[g.reason]}
                                        </Badge>
                                        {g.tradeId && <Text size="xs" c="dimmed">{g.tradeId}</Text>}
                                    </Table.Td>
                                    <Table.Td>{deleteButton(g)}</Table.Td>
                                </Table.Tr>
                            ))}
                            {allocated.map((r) => (
                                <Table.Tr key={r.id}>
                                    <Table.Td>{r.isin}</Table.Td>
                                    <Table.Td>{r.fundName}</Table.Td>
                                    <Table.Td>{r.side}</Table.Td>
                                    <Table.Td>{renderRows({ rows: [r] })}</Table.Td>
                                    <Table.Td><NumberFormatter value={r.amount} thousandSeparator decimalScale={2} /></Table.Td>
                                    <Table.Td>
                                        <Badge color="green" variant="light">Allocated</Badge>
                                        <Text size="xs" c="dimmed">{r.allocatedTradeId}</Text>
                                    </Table.Td>
                                    <Table.Td></Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Stack>
            </Collapse>

            <GlobalDpmUploadsModal opened={uploadsOpened} setOpened={setUploadsOpened} />
        </Card>
    );
};

/* ========================= Date cards (replaces pending-dates panel) ========================= */

// mantine yellow-6 / blue-6 / green-6 / red-6
const COLOR_PENDING = '#fab005';
const COLOR_ALLOCATED = '#228be6';
const COLOR_SENT = '#40c057';
const COLOR_ERROR = '#fa5252';

interface DateCardsProps {
    selectedDateId?: string;
    onSelectDate: (date: Date) => void;
}

interface DateStates {
    pending: number;
    allocated: number;
    sent: number;
    error: number;
}

export const GlobalDpmDateCards = ({ selectedDateId, onSelectDate }: DateCardsProps) => {
    const db = useFirestore();
    const [trades, setTrades] = useState<Array<Trade & { dateId: string }> | null>(null);
    const [rows, setRows] = useState<GlobalDpmAllocationRow[] | null>(null);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        const q = query(collectionGroup(db, 'trades'), where('accountId', '==', GLOBAL_DPM_ACCOUNT));
        return onSnapshot(q, (snap) =>
            setTrades(snap.docs.map((d) => ({ ...(d.data() as Trade), dateId: d.ref.parent.parent?.id ?? '' }))));
    }, [db]);
    useEffect(() => onSnapshot(collectionGroup(db, ROWS_SUBCOLLECTION), (snap) =>
        setRows(snap.docs.map((d) => ({ ...(d.data() as GlobalDpmAllocationRow), id: d.id })))), [db]);

    const cards = useMemo(() => {
        if (trades === null || rows === null) return [];
        const byDate = new Map<string, DateStates>();
        const get = (dateId: string) => {
            let s = byDate.get(dateId);
            if (!s) {
                s = { pending: 0, allocated: 0, sent: 0, error: 0 };
                byDate.set(dateId, s);
            }
            return s;
        };

        for (const t of trades) {
            /* the DPM team thinks in ORDER dates; the doc's bucket is the settlement date */
            const s = get(t.orderDateId ?? t.dateId);
            const allocatedQty = (t.allocations ?? []).reduce((sum, a) => sum + a.qty, 0);
            if (ALLFUNDS_DEAD_STATUSES.includes(t.allfundsStatus ?? '')) s.error++;
            else if (t.state === 'LOCKED') s.sent++;
            // "allocated" (ready to send) only when FULLY allocated — a partial
            // allocation still needs work, so it stays pending
            else if (t.filledQty > 0 && qtyEq(allocatedQty, t.filledQty)) s.allocated++;
            else s.pending++; // in-flight at AllFunds, unallocated, or partially allocated
        }

        /* pending rows: per-date matcher run — mismatches are errors, no-trade
           groups count as expected trades still pending */
        const pendingRowsByDate = new Map<string, GlobalDpmAllocationRow[]>();
        rows.filter((r) => r.status === 'pending').forEach((r) => {
            (pendingRowsByDate.get(r.valueDate) ?? pendingRowsByDate.set(r.valueDate, []).get(r.valueDate)!).push(r);
        });
        // the matcher keys trades by their order date itself, so all trades go in every run
        const matchableTrades = trades.map((t) => tradeToMatchable(t, t.dateId));
        for (const [dateId, dateRows] of pendingRowsByDate) {
            const match = matchGlobalDpmRows(dateRows, matchableTrades);
            const s = get(dateId);
            s.error += match.mismatches.length + match.ambiguous.length;
            s.pending += match.waiting.filter((g) => g.reason === 'no-trade').length;
        }

        const toTime = (dateId: string) => {
            const [dd, mm, yyyy] = dateId.split('-').map(Number);
            return new Date(yyyy, mm - 1, dd).getTime();
        };
        return [...byDate.entries()]
            .map(([dateId, states]) => ({ dateId, states, total: states.pending + states.allocated + states.sent + states.error }))
            // dates where everything is sent need no attention
            .filter((c) => c.states.pending + c.states.allocated + c.states.error > 0)
            .sort((a, b) => toTime(b.dateId) - toTime(a.dateId));
    }, [trades, rows]);

    if (cards.length === 0) return null;

    const totals = cards.reduce(
        (acc, c) => ({
            pending: acc.pending + c.states.pending,
            allocated: acc.allocated + c.states.allocated,
            error: acc.error + c.states.error,
        }),
        { pending: 0, allocated: 0, error: 0 },
    );

    return (
        <Paper withBorder radius="md" p="xs" mb="md">
            <UnstyledButton w="100%" onClick={() => setExpanded((e) => !e)} aria-expanded={expanded} aria-label="Toggle Global DPM dates">
                <Group justify="space-between" wrap="nowrap">
                    <Group gap="sm" wrap="nowrap">
                        <Text size="sm" fw={600}>Global DPM — {cards.length} date{cards.length > 1 ? 's' : ''} pending</Text>
                        <Text size="xs" c="dimmed">
                            {totals.pending > 0 ? `${totals.pending} pending` : ''}
                            {totals.pending > 0 && totals.allocated > 0 ? ' · ' : ''}
                            {totals.allocated > 0 ? `${totals.allocated} to send` : ''}
                            {(totals.pending > 0 || totals.allocated > 0) && totals.error > 0 ? ' · ' : ''}
                            {totals.error > 0 ? `${totals.error} error` : ''}
                        </Text>
                        {totals.error > 0 && <Badge color="red" variant="light" size="sm">errors</Badge>}
                    </Group>
                    {expanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                </Group>
            </UnstyledButton>

            <Collapse expanded={expanded}>
                <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="sm" verticalSpacing="sm" mt="xs">
                    {cards.map(({ dateId, states, total }) => {
                        const [dd, mm, yyyy] = dateId.split('-').map(Number);
                        const selected = dateId === selectedDateId;
                        const slices = [
                            { id: 'pending', label: 'Pending', value: states.pending, color: COLOR_PENDING },
                            { id: 'allocated', label: 'Allocated', value: states.allocated, color: COLOR_ALLOCATED },
                            { id: 'sent', label: 'Sent', value: states.sent, color: COLOR_SENT },
                            { id: 'error', label: 'Error', value: states.error, color: COLOR_ERROR },
                        ].filter((s) => s.value > 0);

                        return (
                            <Paper
                                key={dateId}
                                p="xs"
                                radius="md"
                                withBorder
                                shadow="xs"
                                onClick={() => onSelectDate(new Date(yyyy, mm - 1, dd))}
                                style={{
                                    minWidth: 0,
                                    cursor: 'pointer',
                                    borderColor: selected ? 'var(--mantine-color-blue-5)' : undefined,
                                }}
                            >
                                <Stack gap={4}>
                                    <Group justify="space-between" gap={8} wrap="nowrap">
                                        <Text size="sm" fw={700} lineClamp={1}>{dateId}</Text>
                                        <Text size="10px" c="dimmed" fw={600}>{total} trade{total === 1 ? '' : 's'}</Text>
                                    </Group>

                                    <PieChart
                                        series={[
                                            {
                                                data: slices.map(({ id, label, value }) => ({ id, label, value })),
                                                innerRadius: 20,
                                                outerRadius: 40,
                                                paddingAngle: 1,
                                            },
                                        ]}
                                        colors={slices.map((s) => s.color)}
                                        height={92}
                                        margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                                        hideLegend
                                        skipAnimation
                                    />

                                    <Stack gap={2}>
                                        {slices.map((slice) => (
                                            <Group key={slice.id} justify="space-between" gap={6} wrap="nowrap">
                                                <Group gap={6} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                                                    <span
                                                        style={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            backgroundColor: slice.color,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <Text size="10px" lineClamp={1}>{slice.label}</Text>
                                                </Group>
                                                <Text size="10px" c="dimmed" fw={600}>{slice.value}</Text>
                                            </Group>
                                        ))}
                                    </Stack>
                                </Stack>
                            </Paper>
                        );
                    })}
                </SimpleGrid>
            </Collapse>
        </Paper>
    );
};
